import PropTypes from 'prop-types'
import classnames from 'classnames'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import { requestCommunityLifetimeValueLeaderboardReport } from '../../../../../actions/reportsActions'
import { deserialize } from '../../../../../utility/serialization'
import { displayWithCurrency } from '../../../../../utility/currency'
import { COMMON_ERRORS } from '../../../../../misc/Constants'

import Loader from '../../../../common/loader/Loader'
import InfiniteScroll from '../../../../infinite-scroll/InfiniteScroll'
import FamilyLeaderboardRow from './FamilyLeaderboardRow'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './LifetimeValueLeaderboardWidget.module.scss'
import { ReactComponent as OpenEyeIcon } from '../../../../../assets/images/common/icons/open-eye-icon.svg'

const PER_PAGE = 10

const StatCard = ({ value, label }) => (
  <div className={classnames('d-flex flex-column text-center', businessDashboardClasses.statistics)}>
    <span className={businessDashboardClasses.value}>{value}</span>
    <span className={businessDashboardClasses.topLabel}>{label}</span>
  </div>
)

StatCard.propTypes = {
  value: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
}

const formatRetention = (ratio) => (ratio === null ? '—' : `${Math.round(ratio * 100)}%`)
const formatEnrollments = (value) => Number(value ?? 0).toFixed(1)

const LifetimeValueLeaderboardWidget = () => {
  const { t } = useTranslation()
  const community = useSelector(state => state.user.current_user.communities[0])
  const currency = community?.currency

  const [stats, setStats] = useState(null)
  const [families, setFamilies] = useState([])
  const [pagination, setPagination] = useState({ nextPage: 1, totalPages: null })
  const [isLoading, setIsLoading] = useState(true)

  const abortController = useRef(null)

  const fetchPage = (page) =>
    requestCommunityLifetimeValueLeaderboardReport(
      { communityId: community?.id, page, perPage: PER_PAGE },
      abortController.current?.signal
    ).then(res => {
      const data = deserialize(res.data.report.data)

      setStats({
        averageLtvPerStudent: data.averageLtvPerStudent,
        averageEnrollmentsPerStudent: data.averageEnrollmentsPerStudent,
        retentionRateTtm: data.retentionRateTtm,
      })
      setFamilies(prev => (page === 1 ? data.families : [...prev, ...data.families]))
      setPagination({
        nextPage: (data.pagination.page ?? page) + 1,
        totalPages: data.pagination.totalPages ?? null,
      })
    })

  const handleError = (err) => {
    if (!axios.isCancel(err)) {
      toast.error(COMMON_ERRORS[500])
    }
  }

  const handleFetchMore = (nextPage) => {
    fetchPage(nextPage).catch(handleError)
  }

  useEffect(() => {
    setIsLoading(true)
    abortController.current = new AbortController()

    fetchPage(1)
      .catch(handleError)
      .finally(() => setIsLoading(false))

    return () => abortController.current.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community?.id])

  const hasMore = pagination.totalPages !== null && pagination.nextPage <= pagination.totalPages

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={classnames('d-flex justify-content-between align-items-center', businessDashboardClasses.titleWrapper)}>
        <h2 className={businessDashboardClasses.title}>
          {t('dashboard.business.lifetimeValueLeaderboard.lifetimeValueLeaderboardWidget.title')}
        </h2>
        <Link to='/members' className={classes.viewMembersLink}>
          <OpenEyeIcon className={classes.eyeIcon} />
          {t('dashboard.business.lifetimeValueLeaderboard.lifetimeValueLeaderboardWidget.viewMembers')}
        </Link>
      </div>

      {isLoading && !families.length ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>{t('dashboard.business.lifetimeValueLeaderboard.lifetimeValueLeaderboardWidget.loadingData')}</Loader>
        </div>
      ) : (
        stats && (
          <>
            <div className={classnames('d-flex justify-content-around', businessDashboardClasses.sectionWrapper)}>
              <StatCard
                value={displayWithCurrency(stats.averageLtvPerStudent, currency)}
                label={t('dashboard.business.lifetimeValueLeaderboard.lifetimeValueLeaderboardWidget.averageLtvPerStudent')}
              />
              <StatCard
                value={formatEnrollments(stats.averageEnrollmentsPerStudent)}
                label={t('dashboard.business.lifetimeValueLeaderboard.lifetimeValueLeaderboardWidget.avgEnrollmentsPerStudent')}
              />
              <StatCard
                value={formatRetention(stats.retentionRateTtm)}
                label={t('dashboard.business.lifetimeValueLeaderboard.lifetimeValueLeaderboardWidget.retentionRateTtm')}
              />
            </div>

            <div id='lifetimeValueLeaderboard' className={classes.leaderboardList}>
              <InfiniteScroll
                dataLength={families.length}
                next={() => handleFetchMore(pagination.nextPage)}
                hasMore={hasMore}
                scrollableTarget='lifetimeValueLeaderboard'
              >
                {families.map((family, index) => (
                  <FamilyLeaderboardRow
                    key={family.id}
                    rank={index + 1}
                    family={family}
                    currency={currency}
                  />
                ))}
              </InfiniteScroll>
            </div>
          </>
        )
      )}
    </section>
  )
}

export default LifetimeValueLeaderboardWidget
